import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { FachInfo } from "@/components/ui/fach-info";
import { toast } from "sonner";
import { Plus, Trash2, Loader2 } from "lucide-react";
import api from "@/utils/api";

const fmt = (c) => (c / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const emptyForm = (country) => ({
  title: "", purpose: "", destination_place: "",
  start_at: "", end_at: "",
  qualifies_as_reise: true, overnight: false, vehicle_private_confirmed: false,
  legs: [], nights: [], meals: [], business_meals: [], country,
});

const dayList = (startAt, endAt) => {
  if (!startAt || !endAt) return [];
  const days = [];
  let d = new Date(startAt.slice(0, 10));
  const end = new Date(endAt.slice(0, 10));
  while (d <= end && days.length < 62) {
    days.push(d.toISOString().slice(0, 10));
    d = new Date(d.getTime() + 86400000);
  }
  return days;
};

export const TripWizard = ({ open, onClose, country, editTrip, onSaved }) => {
  const [form, setForm] = useState(emptyForm(country));
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const isAT = country === "AT";

  useEffect(() => {
    if (open) setForm(editTrip ? { ...emptyForm(country), ...editTrip } : emptyForm(country));
    if (open) setPreview(null);
  }, [open, editTrip, country]);

  const days = useMemo(() => dayList(form.start_at, form.end_at), [form.start_at, form.end_at]);
  const valid = form.title && form.destination_place && form.start_at && form.end_at;

  useEffect(() => {
    if (!valid) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.post("/travel/preview", form);
        setPreview(res.data);
      } catch { setPreview(null); }
      setLoading(false);
    }, 500);
    return () => clearTimeout(t);
  }, [JSON.stringify(form), valid]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLeg = (i, k, v) => setForm((f) => {
    const legs = [...f.legs]; legs[i] = { ...legs[i], [k]: v }; return { ...f, legs };
  });
  const setDayItem = (list, date, patch) => setForm((f) => {
    const arr = [...(f[list] || [])];
    const idx = arr.findIndex((x) => x.date === date);
    if (idx >= 0) arr[idx] = { ...arr[idx], ...patch };
    else arr.push({ date, ...patch });
    return { ...f, [list]: arr };
  });
  const getDayItem = (list, date) => (form[list] || []).find((x) => x.date === date) || {};

  const save = async () => {
    setSaving(true);
    try {
      if (editTrip?.id) await api.put(`/travel/trips/${editTrip.id}`, form);
      else await api.post("/travel/trips", form);
      toast.success(editTrip ? "Reise aktualisiert" : "Reise als Entwurf gespeichert");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="trip-wizard-dialog">
        <DialogHeader>
          <DialogTitle>{editTrip ? "Reise bearbeiten" : "Neue Geschäftsreise"} ({country})</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Titel *</Label>
              <Input data-testid="trip-title-input" value={form.title}
                     onChange={(e) => set("title", e.target.value)} placeholder="z. B. Kundentermin Linz" />
            </div>
            <div>
              <Label>Zielort *
                <FachInfo title="Zielort" law={isAT ? "§ 4 Abs 5 EStG / EStR" : "§ 9 Abs 4a EStG"} testId="fachinfo-zielort">
                  Der Zielort steuert die {isAT ? "Mittelpunkt-Prüfung (max. 15 Einsatztage/Jahr, 5 bei wöchentlicher Regelmäßigkeit)" : "Dreimonatsfrist (gleiche Tätigkeitsstätte)"}. Gleiche Orte bitte identisch schreiben.
                </FachInfo>
              </Label>
              <Input data-testid="trip-destination-input" value={form.destination_place}
                     onChange={(e) => set("destination_place", e.target.value)} placeholder="z. B. Linz" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Beginn (Datum + Uhrzeit) *</Label>
              <Input type="datetime-local" data-testid="trip-start-input" value={form.start_at}
                     onChange={(e) => set("start_at", e.target.value)} />
            </div>
            <div>
              <Label>Ende (Datum + Uhrzeit) *</Label>
              <Input type="datetime-local" data-testid="trip-end-input" value={form.end_at}
                     onChange={(e) => set("end_at", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Zweck</Label>
            <Input data-testid="trip-purpose-input" value={form.purpose || ""}
                   onChange={(e) => set("purpose", e.target.value)} placeholder="z. B. Projektabnahme" />
          </div>

          {isAT ? (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={form.qualifies_as_reise} data-testid="trip-qualifies-checkbox"
                        onCheckedChange={(v) => set("qualifies_as_reise", !!v)} className="mt-0.5" />
              <span>Reise iSd § 4 Abs 5 EStG: Zielort <strong>über 25 km</strong> vom Betriebsort entfernt
                <FachInfo title="Reise-Definition (AT)" law="§ 4 Abs 5 EStG" testId="fachinfo-reise-at">
                  Tages- und Nächtigungsgeld gibt es nur bei &gt; 25 km Entfernung und &gt; 3 h Dauer. Fahrtkosten (Kilometergeld) sind davon unabhängig immer absetzbar.
                </FachInfo>
              </span>
            </label>
          ) : (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox checked={form.overnight} data-testid="trip-overnight-checkbox"
                        onCheckedChange={(v) => set("overnight", !!v)} className="mt-0.5" />
              <span>Mit auswärtiger Übernachtung
                <FachInfo title="Übernachtung (DE)" law="§ 9 Abs 4a EStG" testId="fachinfo-overnight-de">
                  Bei mehrtägigen Reisen mit Übernachtung gibt es An-/Abreisetag je 14 € ohne Mindestdauer. Übernachtungskosten selbst sind nur per Beleg absetzbar — es gibt KEINE Übernachtungspauschale für Selbständige.
                </FachInfo>
              </span>
            </label>
          )}

          {/* Fahrten */}
          <div className="border rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">Fahrten mit Privat-KFZ
                <FachInfo title="Kilometergeld" law={isAT ? "§ 26 Z 4 EStG iVm KmGV" : "§ 9 Abs 1 Nr. 4a EStG iVm BRKG"} testId="fachinfo-km">
                  {isAT
                    ? "0,50 €/km PKW · 0,25 € Motorrad/Fahrrad · +0,15 € je Mitfahrer. Cap 30.000 km/Jahr. Deckt AUCH Maut, Vignette und Parkgebühren ab."
                    : "0,30 €/km PKW · 0,20 € Motorrad. Parkgebühren und Maut sind ZUSÄTZLICH per Beleg absetzbar. Keine Fahrrad-Pauschale in DE."}
                </FachInfo>
              </Label>
              <Button size="sm" variant="outline" data-testid="trip-add-leg-btn"
                      onClick={() => set("legs", [...form.legs, { vehicle_type: "pkw", km: "", passengers: 0 }])}>
                <Plus className="h-4 w-4 mr-1" /> Fahrt
              </Button>
            </div>
            {form.legs.map((leg, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`trip-leg-row-${i}`}>
                <select className="border rounded-md h-9 px-2 text-sm" value={leg.vehicle_type}
                        data-testid={`trip-leg-vehicle-${i}`}
                        onChange={(e) => setLeg(i, "vehicle_type", e.target.value)}>
                  <option value="pkw">PKW/Kombi</option>
                  <option value="motorrad">Motorrad</option>
                  {isAT && <option value="fahrrad">Fahrrad/E-Bike</option>}
                </select>
                <Input type="number" min="0" placeholder="km" className="w-28" value={leg.km}
                       data-testid={`trip-leg-km-${i}`}
                       onChange={(e) => setLeg(i, "km", e.target.value === "" ? "" : Number(e.target.value))} />
                {isAT && leg.vehicle_type === "pkw" && (
                  <Input type="number" min="0" placeholder="Mitfahrer" className="w-28" value={leg.passengers}
                         data-testid={`trip-leg-pax-${i}`}
                         onChange={(e) => setLeg(i, "passengers", Number(e.target.value || 0))} />
                )}
                <Button size="icon" variant="ghost" data-testid={`trip-leg-remove-${i}`}
                        onClick={() => set("legs", form.legs.filter((_, j) => j !== i))}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
            {form.legs.length > 0 && (
              <label className="flex items-start gap-2 text-sm bg-amber-50 border border-amber-200 rounded-md p-2">
                <Checkbox checked={form.vehicle_private_confirmed} data-testid="trip-vehicle-private-checkbox"
                          onCheckedChange={(v) => set("vehicle_private_confirmed", !!v)} className="mt-0.5" />
                <span>Ich bestätige: Das Fahrzeug ist <strong>Privatvermögen</strong> (betriebliche Nutzung unter 50 %).
                  <FachInfo title="Privatvermögen-Voraussetzung" law={isAT ? "§ 4 EStG / EStR (WKO)" : "§ 4 EStG / R 4.2 EStR"} testId="fachinfo-privatvermoegen">
                    Kilometergeld gibt es nur für Privat-KFZ. Bei ≥ 50 % betrieblicher Nutzung (Betriebsvermögen) sind zwingend die tatsächlichen Kosten per Beleg anzusetzen.
                  </FachInfo>
                </span>
              </label>
            )}
          </div>

          {/* Nächte (AT) */}
          {isAT && days.length > 1 && (
            <div className="border rounded-lg p-3 space-y-2">
              <Label className="font-semibold">Nächtigungen
                <FachInfo title="Nächtigungsgeld" law="§ 26 Z 4 EStG" testId="fachinfo-naechtigung">
                  Je Nacht wahlweise 17 € pauschal (inkl. Frühstück) ODER die tatsächlichen Kosten per Beleg — nie beides.
                </FachInfo>
              </Label>
              {days.slice(0, -1).map((d) => {
                const n = (form.nights || []).find((x) => x.date === d);
                return (
                  <div key={d} className="flex items-center gap-3 text-sm" data-testid={`trip-night-row-${d}`}>
                    <span className="w-24">{d.slice(8, 10)}.{d.slice(5, 7)}.</span>
                    <select className="border rounded-md h-8 px-2 text-sm" value={n ? n.mode : "keine"}
                            data-testid={`trip-night-mode-${d}`}
                            onChange={(e) => {
                              const v = e.target.value;
                              setForm((f) => ({
                                ...f,
                                nights: v === "keine"
                                  ? (f.nights || []).filter((x) => x.date !== d)
                                  : [...(f.nights || []).filter((x) => x.date !== d), { date: d, mode: v }],
                              }));
                            }}>
                      <option value="keine">Keine Übernachtung</option>
                      <option value="pauschal">Pauschal 17,00 €</option>
                      <option value="beleg">Per Beleg (separat erfassen)</option>
                    </select>
                  </div>
                );
              })}
            </div>
          )}

          {/* Mahlzeiten */}
          {days.length > 0 && (
            <div className="border rounded-lg p-3 space-y-2">
              {isAT ? (
                <>
                  <Label className="font-semibold">Geschäftsessen
                    <FachInfo title="Kürzung Geschäftsessen" law="§ 4 Abs 5 EStG / EStR" testId="fachinfo-geschaeftsessen">
                      Jedes betriebliche Mittag- oder Abendessen kürzt das Tagesgeld um 15 € (nie unter 0).
                    </FachInfo>
                  </Label>
                  {days.map((d) => (
                    <div key={d} className="flex items-center gap-3 text-sm">
                      <span className="w-24">{d.slice(8, 10)}.{d.slice(5, 7)}.</span>
                      <Input type="number" min="0" max="2" className="w-20 h-8"
                             data-testid={`trip-bizmeal-${d}`}
                             value={getDayItem("business_meals", d).count || 0}
                             onChange={(e) => setDayItem("business_meals", d, { count: Number(e.target.value || 0) })} />
                      <span className="text-slate-500">Essen</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <Label className="font-semibold">Gestellte Mahlzeiten
                    <FachInfo title="Mahlzeiten-Kürzung" law="§ 9 Abs 4a S. 8 EStG" testId="fachinfo-mahlzeiten">
                      Kürzung immer vom vollen Satz (28 €): Frühstück −5,60 €, Mittag/Abend je −11,20 € — auch an An-/Abreisetagen. Nie unter 0.
                    </FachInfo>
                  </Label>
                  {days.map((d) => {
                    const m = getDayItem("meals", d);
                    return (
                      <div key={d} className="flex items-center gap-4 text-sm">
                        <span className="w-24">{d.slice(8, 10)}.{d.slice(5, 7)}.</span>
                        {[["breakfast", "Frühstück"], ["lunch", "Mittag"], ["dinner", "Abend"]].map(([k, lbl]) => (
                          <label key={k} className="flex items-center gap-1.5">
                            <Checkbox checked={!!m[k]} data-testid={`trip-meal-${k}-${d}`}
                                      onCheckedChange={(v) => setDayItem("meals", d, { [k]: !!v })} />
                            {lbl}
                          </label>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}

          {/* Live-Berechnung */}
          <div className="border-2 border-emerald-200 bg-emerald-50/50 rounded-lg p-3" data-testid="trip-preview-panel">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm">Berechnung (live)</span>
              {loading && <Loader2 className="h-4 w-4 animate-spin text-emerald-600" />}
            </div>
            {!valid && <p className="text-sm text-slate-500">Titel, Zielort und Zeitraum eingeben …</p>}
            {preview && preview.ok === false && (
              <p className="text-sm text-red-600" data-testid="trip-preview-blocked">{preview.blocked_reason}</p>
            )}
            {preview?.ok && (
              <div className="space-y-2">
                {preview.positions.map((p) => (
                  <div key={p.key} className="text-sm" data-testid={`trip-preview-pos-${p.key}`}>
                    <div className="flex justify-between font-medium">
                      <span>{p.label}{p.vat_cents > 0 && <span className="text-slate-500 font-normal"> · VSt {fmt(p.vat_cents)}</span>}</span>
                      <span className="font-mono">{fmt(p.amount_cents)}</span>
                    </div>
                    <div className="text-xs text-slate-500">{p.trace}</div>
                    <div className="text-[10px] text-slate-400">{p.legal_basis}</div>
                  </div>
                ))}
                {preview.warnings.map((w, i) => (
                  <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid={`trip-preview-warning-${i}`}>{w}</p>
                ))}
                <div className="flex justify-between border-t pt-2 font-bold text-sm">
                  <span>Gesamt absetzbar</span>
                  <span className="font-mono" data-testid="trip-preview-total">{fmt(preview.totals.total_cents)}</span>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid="trip-cancel-btn">Abbrechen</Button>
            <Button onClick={save} disabled={!valid || saving || (preview && preview.ok === false)}
                    className="bg-emerald-600 hover:bg-emerald-700" data-testid="trip-save-btn">
              {saving ? "Speichert …" : editTrip ? "Änderungen speichern" : "Als Entwurf speichern"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TripWizard;
